import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import GodkjennOgBookPanel from "../components/GodkjennOgBookPanel";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const KAT_LABELS = { sykkelservice: "Sykkel", skiservice: "Ski" };

const fmtDT = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diffMin = Math.floor((now - dt) / 60000);
  if (diffMin < 1) return "Nå nettopp";
  if (diffMin < 60) return `${diffMin} min siden`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} t siden`;
  return dt.toLocaleString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function ChatDetalj() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [markingUnread, setMarkingUnread] = useState(false);
  const [closing, setClosing] = useState(false);
  const threadRef = useRef(null);

  async function fetchChat() {
    try {
      const r = await fetch(`${API_BASE_URL}/chat/${id}`, { cache: "no-store" });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setChat(data);
    } catch {
      setError("Kunne ikke hente samtalen.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchChat();
  }, [id]); // eslint-disable-line

  // Merk som lest ved åpning
  useEffect(() => {
    if (!chat) return;
    const hasUnread = (chat.messages || []).some((m) => m.from === "customer" && !m.readAt);
    if (hasUnread) {
      fetch(`${API_BASE_URL}/chat/${id}/les`, { method: "PATCH" }).catch(() => {});
    }
  }, [chat, id]);

  // Scroll til bunn av tråd
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [chat?.messages?.length]);

  const handleSend = async () => {
    const tekst = replyText.trim();
    if (!tekst) return;
    setSending(true);
    setSendError("");
    try {
      const r = await fetch(`${API_BASE_URL}/chat/${id}/svar`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tekst }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.message || "Feil");
      }
      setReplyText("");
      await fetchChat();
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleMarkUnread = async () => {
    setMarkingUnread(true);
    try {
      await fetch(`${API_BASE_URL}/chat/${id}/mark-unread`, { method: "PATCH" });
      navigate("/varsling");
    } catch {
      setMarkingUnread(false);
    }
  };

  const handleSteng = async () => {
    if (!window.confirm("Stenge chatten? Kunden vil ikke kunne sende flere meldinger i denne tråden.")) return;
    setClosing(true);
    try {
      const r = await fetch(`${API_BASE_URL}/chat/${id}/steng`, { method: "PATCH" });
      const data = await r.json();
      setChat(data);
    } catch { /* silent */ } finally {
      setClosing(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-gray-400">Laster samtale…</div>;
  if (error) return <p className="text-red-500 text-center mt-8">{error}</p>;
  if (!chat) return null;

  const bikeInfo = [chat.merke, chat.modell].filter(Boolean).join(" ");
  const tjenesteInfo = [KAT_LABELS[chat.kategori] || chat.kategori, chat.tjenesteType].filter(Boolean).join(" · ");
  const messages = chat.messages || [];

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-blue-600 hover:underline mb-2 block"
          >
            ← Tilbake
          </button>
          <h1 className="text-xl font-bold text-gray-900">{chat.navn}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <a href={`tel:${chat.telefon}`} className="text-blue-600 hover:underline font-mono">
              {chat.telefon}
            </a>
            {bikeInfo && (
              <span className="bg-gray-100 rounded px-2 py-0.5 text-xs">{bikeInfo}</span>
            )}
            {tjenesteInfo && (
              <span className="bg-gray-100 rounded px-2 py-0.5 text-xs">{tjenesteInfo}</span>
            )}
            {chat.booket ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                Booket
              </span>
            ) : chat.erStengt ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                Stengt
              </span>
            ) : (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                chat.besvart ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
              }`}>
                {chat.besvart ? "Besvart" : "Venter på svar"}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!chat.erStengt && !chat.booket && (
            <button
              onClick={handleSteng}
              disabled={closing}
              className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {closing ? "…" : "Steng chat"}
            </button>
          )}
          {!chat.erStengt && (
            <button
              onClick={handleMarkUnread}
              disabled={markingUnread}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              {markingUnread ? "…" : "Merk som ulest"}
            </button>
          )}
        </div>
      </div>

      {/* Samtaletråd */}
      <div
        ref={threadRef}
        className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3 max-h-[55vh] overflow-y-auto"
      >
        {messages.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">Ingen meldinger</p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.from === "customer" ? "justify-start" : "justify-end"}`}>
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  m.from === "customer"
                    ? "bg-white border border-gray-200 text-gray-800"
                    : "bg-teal-600 text-white"
                }`}
              >
                <p className="leading-snug">{m.tekst}</p>
                <p className={`text-[10px] mt-1 ${m.from === "customer" ? "text-gray-400" : "text-teal-200"}`}>
                  {m.from === "customer" ? chat.navn : (m.av || "Ansatt")} · {fmtDT(m.createdAt)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Svarfelt eller stengt-melding */}
      {chat.booket ? null : chat.erStengt ? (
        <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-5 text-center space-y-1">
          <p className="text-sm font-medium text-gray-700">Denne chatten er stengt</p>
          <p className="text-xs text-gray-400">Kunden må starte en ny henvendelse for å chatte videre.</p>
        </div>
      ) : (
        <div className="space-y-2">
          <textarea
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-400"
            placeholder="Skriv svar — sendes som SMS med lenke til chatportalen"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend();
            }}
          />
          {sendError && <p className="text-xs text-red-600">{sendError}</p>}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">Ctrl+Enter for å sende</span>
            <button
              onClick={handleSend}
              disabled={!replyText.trim() || sending}
              className="px-5 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
            >
              {sending ? "Sender…" : "Send svar via SMS"}
            </button>
          </div>
        </div>
      )}

      {/* Godkjenn og book tid */}
      {!chat.booket && !chat.erStengt && (
        <GodkjennOgBookPanel
          endpoint={`${API_BASE_URL}/chat/${id}/godkjenn-book`}
          initialKategori={chat.kategori}
          initialTjenesteSlug={chat.tjenesteSlug}
          initialTjenester={chat.tjenester}
          onBooked={(data) => navigate(`/booking/${data.bookingId}`)}
        />
      )}

      {chat.booket && chat.bookingId && (
        <button
          onClick={() => navigate(`/booking/${chat.bookingId}`)}
          className="w-full text-center px-5 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg"
        >
          Åpne booking →
        </button>
      )}
    </div>
  );
}
